/**
 * Feature E07-F03 — "Research dictionaries (autocomplete)" request/response contracts.
 *
 * The cross-package source of truth for the three autocomplete reference lists
 * that feed the Company-Research Intake form (Industry / Sub-category / Locations)
 * and are edited in admin Settings → Company Research → Dictionaries.
 *
 * Stage 2 (Company Research) was NEVER implemented in the live n8n EER app — it
 * existed only as a prototype (`../EER app/prototype/assets/research.data.js` →
 * `MOCK.dictionaries` + `research.proto.js` → `dictCol` / the intake `<datalist>`s)
 * plus the designed table (Prisma model `ResearchDictionary`,
 * `research_dictionaries`). So there is no n8n SQL/Code to port verbatim; this
 * contract is the 1:1 codification of the prototype's data shape onto the real
 * schema, matching the two designed commands from architecture.md §7:
 *
 *   research_get_dictionaries   GET  /research/dictionaries
 *     → { success:true, dictionaries: { industry:[…], sub_category:[…], location:[…] } }
 *       — ONLY active values, each list ordered by sort_order (T02 acceptance #1).
 *
 *   research_save_dictionary    POST /research/dictionaries
 *     body is one of three discriminated actions:
 *       { action:'add',     dict_key, value }            → create a term (UNIQUE
 *           (dict_key,value) ⇒ an existing/soft-deleted pair is NOT duplicated,
 *           it is re-activated) (T02 acceptance #2)
 *       { action:'remove',  dict_key, value }            → DEACTIVATE (is_active=
 *           false), never a hard delete (T02 acceptance #3)
 *       { action:'reorder', dict_key, values:string[] }  → rewrite sort_order to
 *           the given order (T02 acceptance #4)
 *     → { success:true, dictionaries: {…} } — the fresh active snapshot so the UI
 *       (and the Intake datalists) re-render without a second round-trip.
 *
 * The three `dict_key` values are a closed enum — the Prisma column is
 * VARCHAR(30) but the only legal keys are `industry` | `sub_category` | `location`
 * (T01 acceptance #1). `value` is a non-empty trimmed string (≤255 to fit the
 * column).
 *
 * Lives in its OWN file (NOT re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// dict_key — the closed set of dictionary keys
// ---------------------------------------------------------------------------
/**
 * The dictionary keys (T01 acceptance #1: closed enum). The first three are the
 * Intake autocomplete lists. `industry_map` (E07-F02-T12) holds the admin-edited
 * rules that translate a sub-category / industry phrase into ZoomInfo industry
 * filters for discovery — one rule per value:
 *   `<phrase> => <ZoomInfo industry id or name>[; <another>] [| <keyword>]`
 * e.g. `Space Avionics => mfg.aerospace | avionics`. Parsed and validated by
 * packages/api/src/services/researchSubcategoryIndustryMap.ts.
 */
export const DICT_KEYS = [
    'industry',
    'sub_category',
    'location',
    'industry_map',
    // E06-F07-T01 — stage-0 name prefilter rules: `<type> => <term>[; <term>]`
    // (packages/api/src/services/researchNamePrefilter.ts).
    'name_flags',
];
/** zod enum for a `dict_key` — rejects anything outside the three keys. */
export const dictKeySchema = z.enum(DICT_KEYS);
/** Max length of a dictionary value (mirrors the VARCHAR(255) column). */
export const DICT_VALUE_MAX_LENGTH = 255;
/** The validation message a blank term surfaces (UI + service share it). */
export const DICT_VALUE_REQUIRED_MESSAGE = 'Term is required';
/**
 * A single dictionary term on the wire. Trimmed, non-empty, ≤255. The Intake
 * datalists and the Settings chips render this raw string verbatim.
 */
export const dictValueSchema = z
    .string()
    .trim()
    .min(1, DICT_VALUE_REQUIRED_MESSAGE)
    .max(DICT_VALUE_MAX_LENGTH);
// ---------------------------------------------------------------------------
// Read DTO — research_get_dictionaries projection
// ---------------------------------------------------------------------------
/**
 * The active snapshot of all three dictionaries: each key maps to its active
 * values, already ordered by `sort_order` (the server does the ordering — the
 * client renders the array as-is). Keys are ALWAYS present (an empty list, never
 * a missing key) so the UI can map over a stable shape.
 */
export const dictionariesSnapshotSchema = z.object({
    industry: z.array(z.string()),
    sub_category: z.array(z.string()),
    location: z.array(z.string()),
    /** Discovery industry rules (E07-F02-T12); default [] keeps older bodies valid. */
    industry_map: z.array(z.string()).default([]),
    /** Name prefilter rules (E06-F07-T01); default [] keeps older bodies valid. */
    name_flags: z.array(z.string()).default([]),
});
/** GET /research/dictionaries response. */
export const getDictionariesResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    dictionaries: dictionariesSnapshotSchema,
});
// ---------------------------------------------------------------------------
// Write — research_save_dictionary (discriminated add | remove | reorder)
// ---------------------------------------------------------------------------
/** add: create (or re-activate) ONE term under a key. */
export const dictionaryAddSchema = z.object({
    action: z.literal('add'),
    dict_key: dictKeySchema,
    value: dictValueSchema,
});
/** remove: SOFT-deactivate ONE term under a key. */
export const dictionaryRemoveSchema = z.object({
    action: z.literal('remove'),
    dict_key: dictKeySchema,
    value: dictValueSchema,
});
/**
 * reorder: rewrite the `sort_order` of a key's active terms to the supplied
 * order. `values` is the full ordered list of the key's active terms; the
 * service assigns sort_order = index (1-based) in that array. Unknown values are
 * ignored (defensive — a value the client lists that is not an active row is
 * simply skipped).
 */
export const dictionaryReorderSchema = z.object({
    action: z.literal('reorder'),
    dict_key: dictKeySchema,
    values: z.array(dictValueSchema),
});
/** POST /research/dictionaries body — one of the three actions (discriminated). */
export const saveDictionarySchema = z.discriminatedUnion('action', [
    dictionaryAddSchema,
    dictionaryRemoveSchema,
    dictionaryReorderSchema,
]);
/**
 * POST /research/dictionaries response — the fresh active snapshot after the
 * write (so the Settings chips + the Intake datalists re-render in one round-trip).
 */
export const saveDictionaryResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    dictionaries: dictionariesSnapshotSchema,
});
// ---------------------------------------------------------------------------
// Unrecognized sub-categories (E07-F02-T14)
// ---------------------------------------------------------------------------
/**
 * One request sub-category that matches NO `industry_map` rule (not even after
 * case / word-order / plural / near-phrase normalization), so discovery searches
 * it as a raw phrase. Computed live from the requests against the current rules —
 * adding a rule removes it from the list.
 */
export const unmatchedSubcategorySchema = z.object({
    /** The sub-category text as the request stores it (first spelling seen). */
    phrase: z.string(),
    /** How many research requests use it. */
    requestCount: z.number().int().nonnegative(),
    /** Up to 10 of those request ids, newest first. */
    requestIds: z.array(z.number().int()),
    /** A rule stub the admin completes: `<phrase> => `. */
    ruleTemplate: z.string(),
});
/** GET /research/dictionaries/unmatched response. */
export const getUnmatchedSubcategoriesResponseSchema = z.object({
    success: z.literal(true).optional(),
    items: z.array(unmatchedSubcategorySchema),
});
// ---------------------------------------------------------------------------
// UI helper — column metadata (Settings 3-column layout, Intake datalist ids)
// ---------------------------------------------------------------------------
/**
 * Static metadata for the three columns/datalists, mirroring the prototype:
 *   - `title`     — the Settings column header (`dictCol('Industries',…)`);
 *   - `datalistId`— the Intake `<datalist id>` (`dict-industries` / `dict-subcat`
 *     / `dict-geo`, verbatim from `research.proto.js`).
 * Exported so both the Settings card and the Intake autocomplete derive from one
 * source (no drift between which key feeds which field).
 */
export const DICT_COLUMNS = [
    { key: 'industry', title: 'Industries', datalistId: 'dict-industries' },
    { key: 'sub_category', title: 'Sub-categories', datalistId: 'dict-subcat' },
    { key: 'location', title: 'Locations', datalistId: 'dict-geo' },
    {
        key: 'industry_map',
        title: 'ZoomInfo industry map (phrase => industries | keyword)',
        datalistId: 'dict-industry-map',
    },
    {
        key: 'name_flags',
        title: 'Name prefilter flags (type => term; term)',
        datalistId: 'dict-name-flags',
    },
];
//# sourceMappingURL=research-dictionaries.js.map